import type { GridApi } from '@mui/x-data-grid-pro';
import type * as React from 'react';

export type BulkExpansionState = {
  allExpanded: boolean;
  allCollapsed: boolean;
};

type ApiRefKey = React.RefObject<GridApi>;

const DEFAULT_STATE: BulkExpansionState = { allExpanded: false, allCollapsed: true };
const stateByApiRef = new WeakMap<ApiRefKey, BulkExpansionState>();
const listenersByApiRef = new WeakMap<ApiRefKey, Set<() => void>>();

export const getBulkExpansionState = (apiRef: ApiRefKey): BulkExpansionState =>
  stateByApiRef.get(apiRef) ?? DEFAULT_STATE;

export const setBulkExpansionState = (apiRef: ApiRefKey, next: BulkExpansionState): void => {
  const prev = stateByApiRef.get(apiRef) ?? DEFAULT_STATE;
  if (prev.allExpanded === next.allExpanded && prev.allCollapsed === next.allCollapsed) {
    // No change: keep the cached object identity stable for useSyncExternalStore.
    return;
  }
  stateByApiRef.set(apiRef, next);
  const listeners = listenersByApiRef.get(apiRef);
  if (listeners) {
    for (const listener of listeners) listener();
  }
};

export const subscribeBulkExpansionState = (
  apiRef: ApiRefKey,
  listener: () => void,
): (() => void) => {
  let listeners = listenersByApiRef.get(apiRef);
  if (!listeners) {
    listeners = new Set();
    listenersByApiRef.set(apiRef, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) listenersByApiRef.delete(apiRef);
  };
};
